import React from 'react';
import { Outlet } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar,
  Tooltip,
  Stack
} from '@mui/material';
import { 
  Logout as LogoutIcon,
  AccountCircle,
  Settings as SettingsIcon
} from '@mui/icons-material';
import BottomNavigation from '../../components/FieldAgent/BottomNavigation';
import TechnicalNoteSettingsDialog from '../../components/TechnicalNoteSettingsDialog';
import InterventionSettingsDialog from '../../components/InterventionSettingsDialog';
import ConditionNoteSettingsDialog from '../../components/ConditionNoteSettingsDialog';
import ConditionPhotoSettingsDialog from '../../components/ConditionPhotoSettingsDialog';
import { useAppSelector, useAppDispatch } from '../../store';
import { logoutUser } from '../../store/authSlice';
import { useNavigate } from 'react-router-dom';

const HEADER_HEIGHT = 64;
const BOTTOM_NAV_HEIGHT = 64;

const FieldAgentLayout: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = React.useState(false);
  const [showInterventionSettings, setShowInterventionSettings] = React.useState(false);
  const [showConditionNoteSettings, setShowConditionNoteSettings] = React.useState(false);
  const [showConditionPhotoSettings, setShowConditionPhotoSettings] = React.useState(false);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Fixed Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%)',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 0,
          flexShrink: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(45deg, 
                rgba(255, 255, 255, 0.1) 0%, 
                transparent 25%, 
                transparent 75%, 
                rgba(255, 255, 255, 0.05) 100%
              )
            `,
            backgroundSize: '200% 200%',
            animation: 'shimmer 8s ease-in-out infinite',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '0% 0%' },
            '50%': { backgroundPosition: '100% 100%' },
            '100%': { backgroundPosition: '0% 0%' },
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          }
        }}
      >
        <Toolbar 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            height: '100%',
            minHeight: `${HEADER_HEIGHT}px !important`,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Agent Terrain
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {user?.name || 'Utilisateur'}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            {/* User Avatar */}
            <Avatar 
              sx={{ 
                width: { xs: 32, sm: 36 }, 
                height: { xs: 32, sm: 36 }, 
                bgcolor: 'primary.main',
                border: '2px solid',
                borderColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}
            >
              {user?.name?.[0]?.toUpperCase() || <AccountCircle />}
            </Avatar>

            {/* Settings Buttons */}
            <Stack direction="row" spacing={1}>
              <Tooltip title="Paramètres des notes techniques">
                <IconButton 
                  onClick={() => setShowSettings(true)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Paramètres des interventions">
                <IconButton 
                  onClick={() => setShowInterventionSettings(true)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 152, 0, 0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Types de notes (État des lieux)">
                <IconButton 
                  onClick={() => setShowConditionNoteSettings(true)}
                  size="small"
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255, 107, 53, 0.2)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 107, 53, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 53, 0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  📝
                </IconButton>
              </Tooltip>
              <Tooltip title="Types de photos (État des lieux)">
                <IconButton
                  onClick={() => setShowConditionPhotoSettings(true)}
                  size="small"
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(96, 125, 139, 0.25)',
                    border: '1px solid',
                    borderColor: 'rgba(96, 125, 139, 0.35)',
                    '&:hover': {
                      backgroundColor: 'rgba(96, 125, 139, 0.35)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  📷
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Logout Button */}
            <Tooltip title="Déconnexion">
              <IconButton 
                onClick={handleLogout}
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Scrollable Content Area */}
      <Box 
        component="main"
        sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          height: `calc(100vh - ${HEADER_HEIGHT}px - ${BOTTOM_NAV_HEIGHT}px)`,
          backgroundColor: '#f8f9fa',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: 0,
            right: 0,
            bottom: BOTTOM_NAV_HEIGHT,
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(44, 62, 80, 0.08) 1px, transparent 0)
            `,
            backgroundSize: '24px 24px',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100%' }}>
          <Outlet />
        </Box>
      </Box>

      {/* Fixed Bottom Navigation */}
      <BottomNavigation />

      {/* Technical Note Settings Dialog */}
      <TechnicalNoteSettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Intervention Settings Dialog */}
      <InterventionSettingsDialog
        open={showInterventionSettings}
        onClose={() => setShowInterventionSettings(false)}
      />

      {/* Condition Note Settings Dialog */}
      <ConditionNoteSettingsDialog
        open={showConditionNoteSettings}
        onClose={() => setShowConditionNoteSettings(false)}
      />

      <ConditionPhotoSettingsDialog
        open={showConditionPhotoSettings}
        onClose={() => setShowConditionPhotoSettings(false)}
      />
    </Box>
  );
};

export default FieldAgentLayout;
